import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { UserPlusIcon, UserCircleIcon, BuildingOfficeIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';

import { Card } from '../design-system/components/Card';
import { Heading, Text } from '../design-system/components/Typography';
import { IconWrapper } from '../design-system/components/Decorative';
import { Stack, Grid } from '../design-system/components/Layout';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { hexToRgba } from '../utils/colorUtils';

const RegisterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 70px);
  max-height: calc(100vh - 70px);
  min-height: calc(100vh - 70px);
  position: relative;
  background: ${props => props.theme.colors.surface};
  overflow: hidden;

  ${breakpoints.down.md} {
    height: auto;
    max-height: none;
    min-height: calc(100vh - 70px);
    overflow: visible;
  }
`;

const FormPanel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[6]} ${spacing[4]};
  position: relative;
  overflow-y: auto;

  ${breakpoints.down.md} {
    align-items: flex-start;
    padding: ${spacing[5]} ${spacing[4]} ${spacing[10]};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: ${props => props.theme.colors.icomBlue};
  }
`;

const SelectionCardWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  z-index: 1;
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${spacing[6]};
`;

const Section = styled.section`
  margin-bottom: ${spacing[8]};

  &:last-of-type {
    margin-bottom: ${spacing[6]};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing[4]};
  padding-bottom: ${spacing[3]};
  border-bottom: 1px solid ${props => hexToRgba(props.theme.colors.icomBlue || props.theme.colors.primary, 0.12)};
`;

const SectionTitle = styled(Heading)`
  margin-bottom: ${spacing[1]};
`;

const OptionGrid = styled(Grid)`
  margin-bottom: 0;
`;

const OptionCardWrapper = styled.div`
  height: 100%;
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    &::after {
      width: 100%;
    }
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const HeadingWithSmallMargin = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const TextCentered = styled(Text)`
  text-align: center;
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[8]};
`;

const OptionCardLink = styled(Link)`
  display: block;
  height: 100%;
  text-decoration: none;
  color: inherit;
  border-radius: inherit;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 3px;
  }
`;

const OptionCard = styled(Card)`
  padding: ${spacing[6]};
  height: 100%;
  text-align: center;
  cursor: pointer;
`;

const FooterDiv = styled.div`
  text-align: center;
  padding-top: ${spacing[6]};
  border-top: 1px solid ${props => hexToRgba('#000000', 0.1)};
`;

type RegisterOption = {
  to: string;
  icon: typeof UserPlusIcon;
  iconColor: 'primary' | 'accent' | 'secondary';
  titleKey: string;
  descriptionKey: string;
};

const Register: React.FC = () => {
  const { t } = useTranslation();

  const newMemberOptions: RegisterOption[] = [
    {
      to: '/register-individual',
      icon: UserPlusIcon,
      iconColor: 'primary',
      titleKey: 'register.petitionNewMember',
      descriptionKey: 'register.petitionNewMemberDescription',
    },
    {
      to: '/register-institution',
      icon: BuildingOfficeIcon,
      iconColor: 'accent',
      titleKey: 'register.petitionNewOrganization',
      descriptionKey: 'register.petitionNewOrganizationDescription',
    },
  ];

  const platformRegistrationOptions: RegisterOption[] = [
    {
      to: '/register-existing-member',
      icon: UserCircleIcon,
      iconColor: 'secondary',
      titleKey: 'register.registerExistingMember',
      descriptionKey: 'register.registerExistingMemberDescription',
    },
    {
      to: '/register-existing-institution',
      icon: BuildingLibraryIcon,
      iconColor: 'secondary',
      titleKey: 'register.registerExistingInstitution',
      descriptionKey: 'register.registerExistingInstitutionDescription',
    },
  ];

  const sections = [
    {
      titleKey: 'register.sectionNewMemberApplication',
      descriptionKey: 'register.sectionNewMemberApplicationDescription',
      options: newMemberOptions,
    },
    {
      titleKey: 'register.sectionPlatformRegistration',
      descriptionKey: 'register.sectionPlatformRegistrationDescription',
      options: platformRegistrationOptions,
    },
  ];

  const renderOption = ({ to, icon: Icon, iconColor, titleKey, descriptionKey }: RegisterOption) => (
    <OptionCardWrapper key={to}>
      <OptionCardLink to={to}>
        <OptionCard
          variant="elevated"
          hoverElevation
          accentBorder="top"
          pattern="dots"
          patternIntensity="subtle"
        >
          <Stack direction="column" spacing="md" align="center">
            <IconWrapper size="xl" color={iconColor}>
              <Icon style={{ width: '32px', height: '32px' }} aria-hidden />
            </IconWrapper>
            <Heading variant="h4" color="text">
              {t(titleKey)}
            </Heading>
            <TextCentered color="secondary" size="sm">
              {t(descriptionKey)}
            </TextCentered>
          </Stack>
        </OptionCard>
      </OptionCardLink>
    </OptionCardWrapper>
  );

  return (
    <RegisterContainer>
      <FormPanel>
        <SelectionCardWrapper>
          <CardWithPadding 
            variant="elevated" 
            headerVariant="gradient"
            accentBorder="top"
            pattern="dots"
            patternIntensity="subtle"
          >
            <Header>
              <HeadingWithSmallMargin variant="h2" color="text">
                {t('register.selectRegistrationType')}
              </HeadingWithSmallMargin>
            </Header>

            {sections.map(({ titleKey, descriptionKey, options }) => (
              <Section key={titleKey} aria-labelledby={titleKey}>
                <SectionHeader>
                  <SectionTitle id={titleKey} variant="h3" color="text">
                    {t(titleKey)}
                  </SectionTitle>
                  <Text color="secondary" size="sm">
                    {t(descriptionKey)}
                  </Text>
                </SectionHeader>
                <OptionGrid columns={{ sm: 1, md: 2 }} gap="lg">
                  {options.map(renderOption)}
                </OptionGrid>
              </Section>
            ))}

            <FooterDiv>
              <Text size="sm" color="secondary">
                {t('register.haveAccount')}{' '}
                <StyledLink to="/login">{t('register.signIn')}</StyledLink>
              </Text>
            </FooterDiv>
          </CardWithPadding>
        </SelectionCardWrapper>
      </FormPanel>
    </RegisterContainer>
  );
};

export default Register;
